import { NvCheckIcon } from "@/components/navigator";
import { cn } from "@/lib/utils";
import type { DetailTrust } from "@/services/navigator/agent-detail-service";

import { DetailLabel, DetailSection } from "./detail-parts";

// 보안과 신뢰 (설계서 slide 44 · Figma 03-01/04/05): 신뢰 항목 Pill 2종(실 운영 검증 = 채움 · 운영 검증 전 = 점선, 미지원은 서버가 뺌),
// 2차까지는 보안 요약 문단 · 보안 요소 · 제품 보안 인증, 1차+WF · 1차만은 Pill + 회사 보유 인증만. 요약이 없으면 Pill 패널을 전체 폭으로.

function caption(data: DetailTrust) {
  const parts = [data.verified > 0 && `실 운영 검증 ${data.verified}`, data.supported > 0 && `운영 검증 전 ${data.supported}`].filter(Boolean);
  return parts.join(", ");
}

function PillPanel({ data, wide }: { data: DetailTrust; wide: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-3 self-start rounded-nv-control bg-nv-bg-page p-[22px]">
      <span className="text-[14px] leading-[22px] text-nv-text-secondary">지원하는 신뢰 항목</span>
      <DetailLabel>{caption(data)}</DetailLabel>
      <div className={cn("flex flex-wrap gap-2", wide && "max-w-[1000px]")}>
        {data.pills.map((p) => (
          <span
            key={p.label}
            className={cn(
              "inline-flex h-7 items-center gap-1 rounded-nv-control border px-[10px] text-[13px] leading-[18px] text-nv-text-primary",
              p.state === "verified" ? "border-[#E2E5E9] bg-nv-surface" : "border-dashed border-[#B4B7BC] bg-transparent",
            )}
          >
            {p.state === "verified" && <NvCheckIcon size={12} className="text-nv-text-primary" />}
            {p.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// tight: 1차만은 제목 → 신뢰 항목 패널이 24 (Figma ver.7 03-05)
export function TrustSection({ data, tight }: { data: DetailTrust; tight?: boolean }) {
  const hasPills = data.pills.length > 0;
  const hasText = !!(data.summary || data.elements.length || data.product_certifications.length || data.certifications.length);
  return (
    <DetailSection id="trust" title="보안과 신뢰" tight={tight}>
      <div className={cn("grid gap-6", hasPills && hasText ? "lg:grid-cols-[363px_minmax(0,1fr)]" : "grid-cols-1")}>
        {hasPills && <PillPanel data={data} wide={!hasText} />}
        {hasText && (
          <div className="flex min-w-0 flex-col">
            {data.summary && <p className="whitespace-pre-line pb-6 text-[18px] leading-[30px] text-nv-text-primary xl:text-[22px] xl:leading-[34px]">{data.summary}</p>}
            {data.elements.length > 0 && (
              <div className={cn("flex flex-col gap-[10px] pb-[26px] pt-[15px]", data.summary && "border-t border-nv-border")}>
                {data.elements.map((e, i) => (
                  <p key={i} className="text-[15px] leading-6 text-nv-text-primary">
                    {e.label && <strong className="mr-2 font-medium">{e.label}</strong>}
                    {e.text}
                  </p>
                ))}
              </div>
            )}
            {data.product_certifications.length > 0 && (
              <div className="flex flex-col gap-[6px] border-t border-nv-border pb-[26px] pt-[15px]">
                <DetailLabel>제품 보안 인증</DetailLabel>
                <div className="flex flex-wrap gap-[6px]">
                  {data.product_certifications.map((c) => (
                    <span key={c} className="inline-flex h-7 items-center rounded-nv-control border border-[#E2E5E9] bg-nv-surface px-[10px] text-[13px] text-nv-text-primary">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {data.certifications.length > 0 && (
              <div className={cn("flex flex-col gap-[3px] pt-[15px]", (data.summary || data.elements.length > 0 || data.product_certifications.length > 0) && "border-t border-nv-border")}>
                <DetailLabel>회사 보유 인증</DetailLabel>
                <p className="text-[15px] leading-6 text-nv-text-primary">{data.certifications.join(", ")}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </DetailSection>
  );
}
